import React from 'react';
import { AudienceVocabulary, AudiencePicker, AudienceOptions, bulkAudienceUpdates } from './AudienceControls';
import API_BASE_URL from '../../config';
import { useAuth } from '../../context/AuthContext';

/**
 * Sources tab (plan W7): every document the assistant knows — filter by type
 * and status, search, see where each PDF/video is linked from (found_on), and
 * toggle documents out of retrieval (effective within ~30s; excluded docs are
 * skipped by future crawls and restored from stored segments on re-enable).
 */

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const chip = {
  display: 'inline-block', fontSize: '0.68rem', padding: '1px 8px', borderRadius: 999,
  background: '#f3f4f6', color: '#374151', marginRight: 4,
};
const TYPE_LABEL = { webpage: 'Page', pdf: 'PDF', transcript: 'Video' };

export default function SourcesTab() {
  const { channelUrl, customerKey } = useAuth();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [audienceFilter, setAudienceFilter] = React.useState('all');
  const [selected, setSelected] = React.useState([]);
  const [bulkMode, setBulkMode] = React.useState('set');
  const [bulkAudiences, setBulkAudiences] = React.useState([]);
  const [query, setQuery] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);
  const [notice, setNotice] = React.useState('');

  const headers = React.useMemo(
    () => ({ 'Content-Type': 'application/json', 'X-Customer-Key': customerKey }),
    [customerKey]
  );

  const load = React.useCallback(() => {
    fetch(`${API_BASE_URL}/api/corpus/sources?corpus_id=${encodeURIComponent(channelUrl)}`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(`Could not load sources (${e.message}).`));
  }, [channelUrl, headers]);
  React.useEffect(load, [load]);

  const toggle = async (doc) => {
    const excluding = !doc.excluded;
    if (excluding && !window.confirm(
      `Disable "${doc.title}"?\n\nAnswers stop using this source within about 30 seconds. ` +
      'Future crawls skip it until you re-enable it.')) return;
    setBusyId(doc.doc_id); setNotice('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/corpus/sources/set_excluded`, {
        method: 'POST', headers,
        body: JSON.stringify({ corpus_id: channelUrl, doc_id: doc.doc_id, excluded: excluding }),
      });
      const body = await r.json();
      if (!r.ok) {
        setNotice(body.error === 'segments_missing'
          ? `"${doc.title}" can't be re-enabled from storage — re-run the crawl to restore it.`
          : `Failed: ${body.error || r.status}`);
        return;
      }
      setData((d) => ({
        ...d,
        totals: { ...d.totals, excluded: d.totals.excluded + (excluding ? 1 : -1) },
        docs: d.docs.map((x) => (x.doc_id === doc.doc_id ? { ...x, excluded: body.excluded } : x)),
      }));
    } catch (e) {
      setNotice(`Failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const send = async (path, payload, method = 'POST') => {
    const r = await fetch(`${API_BASE_URL}/api/corpus/${path}`, {
      method, headers, body: JSON.stringify({ corpus_id: channelUrl, ...payload }),
    });
    const body = await r.json();
    if (!r.ok) throw new Error(body.errors?.join('; ') || (body.doc_counts
      ? `Untag documents first: ${Object.entries(body.doc_counts).map(([id, n]) => `${id}: ${n}`).join(', ')}`
      : body.hint || body.error || `HTTP ${r.status}`));
    return body;
  };
  const applyResults = (results) => {
    setData((d) => ({ ...d, docs: d.docs.map((doc) => {
      const result = results.find((r) => r.doc_id === doc.doc_id && r.ok);
      return result ? { ...doc, audiences: result.audiences } : doc;
    }) }));
    setSelected((ids) => ids.filter((id) => !results.some((r) => r.doc_id === id && r.ok)));
  };
  const tag = async (doc, audiences) => {
    setBusyId(doc.doc_id); setNotice('');
    try {
      const body = await send('sources/set_audiences', { doc_id: doc.doc_id, audiences });
      applyResults([{ ...body, ok: true }]);
      setNotice('Audience tags saved. Search updates within 30–60 seconds.');
    } catch (e) { setNotice(`Failed: ${e.message}`); }
    finally { setBusyId(null); }
  };
  const bulk = async () => {
    setBusyId('bulk'); setNotice('Updating audience tags…');
    try {
      const results = await bulkAudienceUpdates(selected, bulkAudiences, bulkMode,
        (payload) => send('sources/set_audiences_bulk', payload),
        (batch, done, total) => { applyResults(batch); setNotice(`Processed ${done} of ${total} documents…`); });
      const failed = results.filter((r) => !r.ok);
      setNotice(failed.length ? `${failed.length} documents failed and remain selected. ${failed[0].error}. Retry to finish.`
        : 'Audience tags saved. Search updates within 30–60 seconds.');
    } catch (e) { setNotice(`Failed: ${e.message}. Remaining documents stay selected for retry.`); }
    finally { setBusyId(null); }
  };

  if (error) return <p style={{ color: '#dc2626', fontSize: '0.9rem' }}>{error}</p>;
  if (!data) return <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading sources…</p>;

  const audiences = data.audiences || [];
  const q = query.trim().toLowerCase();
  const rows = data.docs.filter((d) =>
    (audienceFilter === 'all' || (audienceFilter === 'everyone' ? !(d.audiences || []).length : (d.audiences || []).includes(audienceFilter.slice(3)))) &&
    (typeFilter === 'all' || d.source_type === typeFilter) &&
    (statusFilter === 'all' || (statusFilter === 'disabled') === !!d.excluded) &&
    (!q || (d.title || '').toLowerCase().includes(q) || (d.canonical_url || '').toLowerCase().includes(q)));

  return (
    <div style={{ fontFamily: fontStack }}>
      <AudienceVocabulary audiences={audiences} docs={data.docs} disabled={!!busyId}
        onSave={async (next) => {
          const body = await send('audiences', { audiences: next }, 'PUT');
          setData((d) => ({ ...d, audiences: body.audiences }));
          setAudienceFilter('all'); setBulkAudiences([]);
        }} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
               placeholder="Filter sources…" aria-label="Filter sources"
               style={{ flex: '1 1 220px', padding: '8px 10px', fontSize: '0.85rem', fontFamily: fontStack,
                        borderRadius: 8, border: '1px solid #e2e2e2', background: '#fafafa' }} />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Type filter"
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e2e2', fontFamily: fontStack, fontSize: '0.82rem' }}>
          <option value="all">All types ({data.totals.documents})</option>
          <option value="webpage">Pages ({data.totals.by_type.webpage || 0})</option>
          <option value="pdf">PDFs ({data.totals.by_type.pdf || 0})</option>
          <option value="transcript">Videos ({data.totals.by_type.transcript || 0})</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter"
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e2e2', fontFamily: fontStack, fontSize: '0.82rem' }}>
          <option value="all">All statuses</option>
          <option value="enabled">Enabled ({data.totals.documents - data.totals.excluded})</option>
          <option value="disabled">Disabled ({data.totals.excluded})</option>
        </select>
        <select aria-label="Audience filter" value={audienceFilter} onChange={(e) => setAudienceFilter(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e2e2' }}>
          <option value="all">All audiences</option><option value="everyone">Everyone</option>
          {audiences.map((a) => <option key={a.id} value={`id:${a.id}`}>{a.label}</option>)}
        </select>
        <span style={{ fontSize: '0.75rem', color: '#888' }}>{rows.length} shown</span>
      </div>
      {notice && <p role="alert" style={{ color: '#b45309', fontSize: '0.82rem' }}>{notice}</p>}

      {selected.length > 0 && <div aria-label="Bulk audience actions" style={{ padding: 12, background: '#f5f8ff', borderRadius: 8, marginBottom: 12 }}>
        <strong>{selected.length} selected</strong>{' '}
        <select aria-label="Bulk audience mode" value={bulkMode} onChange={(e) => setBulkMode(e.target.value)} disabled={!!busyId}>
          <option value="set">Set audiences</option><option value="add">Add audiences</option><option value="remove">Remove audiences</option>
        </select>
        <AudienceOptions audiences={audiences} value={bulkAudiences} onChange={setBulkAudiences} disabled={!!busyId} />
        {bulkMode === 'set' && !bulkAudiences.length && <p style={{ fontSize: 12 }}>Set to Everyone (clear existing tags).</p>}
        <button type="button" onClick={bulk} disabled={!!busyId || (bulkMode !== 'set' && !bulkAudiences.length)}>
          {busyId === 'bulk' ? 'Updating…' : 'Apply to selected'}</button>{' '}
        <button type="button" disabled={!!busyId} onClick={() => setSelected([])}>Clear selection</button>
      </div>}
      <div style={{ border: '1px solid #eee', borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: '#fafafa', textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', width: 24 }}><input type="checkbox" aria-label="Select all in view"
                disabled={!!busyId || !rows.length} checked={rows.length > 0 && rows.every((d) => selected.includes(d.doc_id))}
                onChange={(e) => setSelected(e.target.checked ? [...new Set([...selected, ...rows.map((d) => d.doc_id)])]
                  : selected.filter((id) => !rows.some((d) => d.doc_id === id)))} /></th>
              <th style={{ padding: '8px 10px' }}>Source</th>
              <th style={{ padding: '8px 10px' }}>Audience</th>
              <th style={{ padding: '8px 10px', width: 60 }}>Type</th>
              <th style={{ padding: '8px 10px', width: 220 }}>Linked from</th>
              <th style={{ padding: '8px 10px', width: 90 }}>Indexed</th>
              <th style={{ padding: '8px 10px', width: 96 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.doc_id} style={{ borderTop: '1px solid #f3f3f3', opacity: d.excluded ? 0.55 : 1 }}>
                <td style={{ padding: '7px 10px' }}><input type="checkbox" aria-label={`Select ${d.title}`}
                  disabled={!!busyId} checked={selected.includes(d.doc_id)}
                  onChange={(e) => setSelected(e.target.checked ? [...selected, d.doc_id] : selected.filter((id) => id !== d.doc_id))} /></td>
                <td style={{ padding: '7px 10px', maxWidth: 380 }}>
                  <a href={d.canonical_url} target="_blank" rel="noopener noreferrer"
                     style={{ color: '#1e5eb8', textDecoration: 'none', display: 'block',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.title}
                  </a>
                </td>
                <td style={{ padding: '7px 10px' }} aria-busy={busyId === d.doc_id}>
                  <AudiencePicker doc={d} audiences={audiences} onSave={(ids) => tag(d, ids)} disabled={!!busyId} />
                  {busyId === d.doc_id && <span role="status">Saving…</span>}
                </td>
                <td style={{ padding: '7px 10px' }}><span style={chip}>{TYPE_LABEL[d.source_type] || d.source_type}</span></td>
                <td style={{ padding: '7px 10px' }}>
                  {(d.found_on || []).length === 0
                    ? <span style={{ color: '#bbb', fontSize: '0.72rem' }}>
                        {d.source_type === 'webpage' ? '—' : 'Not linked from any crawled page'}
                      </span>
                    : (d.found_on.slice(0, 2).map((u) => (
                        <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ ...chip, textDecoration: 'none' }}
                           title={u}>
                          {u.replace(/^https?:\/\/(www\.)?/, '').slice(0, 34)}
                        </a>
                      )))}
                  {(d.found_on || []).length > 2 && <span style={{ fontSize: '0.7rem', color: '#888' }}>+{d.found_on.length - 2} more</span>}
                </td>
                <td style={{ padding: '7px 10px', color: '#777' }}>
                  {d.last_indexed ? new Date(d.last_indexed * 1000).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                  <button type="button" onClick={() => toggle(d)} disabled={!!busyId}
                          aria-label={`${d.excluded ? 'Enable' : 'Disable'} ${d.title}`}
                          style={{ padding: '4px 12px', fontSize: '0.75rem', borderRadius: 999, cursor: 'pointer',
                                   fontFamily: fontStack,
                                   border: d.excluded ? '1px solid #16a34a' : '1px solid #e2e2e2',
                                   background: d.excluded ? '#f0fdf4' : '#fff',
                                   color: d.excluded ? '#15803d' : '#555' }}>
                    {busyId === d.doc_id ? '…' : d.excluded ? 'Enable' : 'Disable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
